import type { EChartsOption } from "echarts";
import type { KpiChartType } from "@verity/shared-types/chart-styles";

import { escapeHtml } from "./chartTooltip";
import { DELTA_NEGATIVE_COLOR, DELTA_POSITIVE_COLOR } from "./comparisonDelta";
import { MINUS, formatCompact, formatGapMagnitude, formatHeadline, formatShare } from "./pictureFormat";
import { targetReasonSentence } from "./targetReason";
import type { BackendChartSpec } from "./types";

/**
 * The KPI pictures: single-value tiles drawn as a chart instead of as the
 * metric card (plan 2026-10-05 finance chart types, D1(a)).
 *
 * A bullet or a gauge is a `kpi` block, not a `chart` block, so it takes none
 * of the chart route's stations: it is not in `CHART_STYLES`, not in
 * `CHART_RECIPES` and not in `ECHARTS_SERIES_CHART_TYPES`. This file is its
 * leg instead, with the same two guarantees the chart route has:
 *
 * - The builder map's key type is `KpiChartType | PreparedLibraryKpiChartType`,
 *   and `KpiChartType` derives from the registry mirror, so a KPI row that
 *   flips without a builder here is a COMPILE error, not a tile that draws
 *   bars.
 * - The backend test `test_composition_block_type_resolution.py` reads
 *   `PREPARED_LIBRARY_KPI_CHART_TYPES` below and fails when a KPI row names a
 *   `dashboard_chart_type` that neither list carries.
 *
 * The routing (`routeChartType`) answers "echarts" for every key of the map,
 * and `buildEchartsOption` hands the spec here before its cartesian assembly.
 */

/**
 * KPI rows the registry still has on `status="library"` whose builder exists
 * here. Same role as `PREPARED_LIBRARY_CHART_TYPES` in the option builder: a
 * library row is filtered out of `KPI_CHART_TYPES`, so the map below could
 * not name it otherwise. A row leaves this list at its flip.
 *
 * Keep prose OUT of the brackets: the backend test extracts every
 * double-quoted token between them.
 */
export const PREPARED_LIBRARY_KPI_CHART_TYPES = [] as const;
type PreparedLibraryKpiChartType = (typeof PREPARED_LIBRARY_KPI_CHART_TYPES)[number];

export type KpiPictureInput = {
  spec: BackendChartSpec;
  /** The bar's (or the arc's) colour when no target judges it: the palette's
   *  first entry, picked by the option builder (this module never indexes a
   *  palette, ADR-0476). */
  neutralColor: string;
  /**
   * A dashboard's resolved colours for this picture (ADR-0476). Absent, the
   * picture draws exactly what it drew before.
   *  - `measureOverride`: the chart's own override on `m:<key>`; it paints the
   *    bar whatever the target says.
   *  - `measure`: the override or the dashboard pin; it replaces only the
   *    neutral colour. A pin never overrides the on/off-track colour, which is
   *    a signal (step 6 review, Arjen's decision via the coordinator).
   *  - `target`: `r:target`, the plan's tick.
   */
  seriesColors?: { measureOverride?: string; measure?: string; target?: string };
  /** Told the colour the bar and (when drawn) the tick were painted in, for
   *  the builder's `drawnSeriesColors`. For the bar that is its SERIES colour
   *  (the override, the pin or the neutral), not an on/off-track colour a
   *  guardrail puts over it: a status is a signal, not the series' colour,
   *  and the Format pane must never offer it as one to keep or pin
   *  (`pictureStatusRef`). */
  painted?: (part: "measure" | "target", color: string) => void;
  /** The tile's number format, applied the same way the tooltips do. */
  formatValue: (value: number) => string;
};

type KpiPictureBuilder = (input: KpiPictureInput) => EChartsOption;

/**
 * The target a picture draws, as the server stamps it on the spec
 * (`services/targets/picture_target.py::resolve_picture_target`, ADR-0341).
 * `value` is null when nothing resolved, and `reason` then says why.
 */
export type PictureTarget = {
  value: number | null;
  kind: string;
  comparator: "lte" | "gte" | null;
  to_date: boolean;
  reason: string | null;
  /** The plan's lines lie outside the period (B1): "No target for this period". */
  lines_range: { start: string; end: string } | null;
};

const finite = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/**
 * The stamp, coerced. `chart_spec_json` is member-authorable and a cache can
 * predate the stamp, so a malformed or absent one reads as "no target, reason
 * unknown" and never steers the colour.
 */
export function readPictureTarget(spec: BackendChartSpec): PictureTarget {
  const raw: unknown = spec.picture_target;
  const t = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const comparator = t.comparator === "lte" || t.comparator === "gte" ? t.comparator : null;
  return {
    value: finite(t.value),
    kind: typeof t.kind === "string" ? t.kind : "target",
    comparator,
    to_date: t.to_date === true,
    reason: typeof t.reason === "string" ? t.reason : null,
    lines_range: linesRangeOf(t.lines_range),
  };
}

/** A `{start, end}` of ISO dates, or null: the stamp is member-authorable. */
export function linesRangeOf(raw: unknown): { start: string; end: string } | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  return typeof r.start === "string" && typeof r.end === "string" && iso.test(r.start) && iso.test(r.end)
    ? { start: r.start, end: r.end }
    : null;
}

const KIND_LABEL: Record<string, string> = {
  target: "Target",
  budget: "Budget",
  guardrail: "Guardrail",
};

/** "Target", "Budget to date": the word the tick's label and tooltip carry. */
export function pictureTargetLabel(target: PictureTarget): string {
  const word = KIND_LABEL[target.kind] ?? "Target";
  return target.to_date ? `${word} to date` : word;
}

/**
 * The bar's colour. Only a GUARDRAIL says which side of the line is good
 * (`lte`: at most, `gte`: at least); a target or a budget has no comparator
 * (`services/targets/service.py` refuses one), and whether being over it is
 * good depends on the metric, so those stay the tile's own colour rather than
 * a guessed green or red.
 */
export function bulletBarColor(actual: number | null, target: PictureTarget, neutral: string): string {
  if (actual == null || target.value == null || target.comparator == null) return neutral;
  const onTrack = target.comparator === "lte" ? actual <= target.value : actual >= target.value;
  return onTrack ? DELTA_POSITIVE_COLOR : DELTA_NEGATIVE_COLOR;
}

/**
 * The measure ref of a KPI picture whose bar a target JUDGES (a guardrail
 * with a value and an actual: `bulletBarColor` paints it on/off-track green
 * or red), or null. Unless the chart overrides `m:<key>`, that bar shows the
 * status, not the series colour, and a pin never replaces it (ADR-0476, step
 * 6 decision). The Format pane asks this so it neither pins nor keeps a
 * status as if it were the series' colour.
 */
export function pictureStatusRef(spec: BackendChartSpec): string | null {
  if (!isKpiPictureChartType(String(spec.chart_type ?? ""))) return null;
  const key = spec.y_keys?.[0] ?? "";
  if (!key) return null;
  const actual = finite(spec.rows?.[0]?.[key]);
  const target = readPictureTarget(spec);
  return bulletBarColor(actual, target, "") === "" ? null : `m:${key}`;
}

/** A round axis end a little past the larger of the two values: the bullet's
 *  scale when it has no positive plan to scale on (D1). */
function axisMax(values: number[]): number | undefined {
  const top = Math.max(...values);
  if (!(top > 0)) return undefined;
  const raw = top * 1.1;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  return Math.ceil(raw / magnitude) * magnitude;
}

// The pictures' ink (plan 2026-10-16-gauge-and-bullet-redesign, D7): the
// mockup's, for the light card customers see (`main.tsx` pins the light
// scheme). A dashboard tile hands ECharts no theme, so these do not follow a
// dark card; `2026-10-16-dashboard-tiles-are-illegible-in-dark-mode.md` makes
// the scheme a builder input and replaces them here, in one place.
/** The number. */
const INK = "#1f2a44";
/** The eyebrow, the share line, the footer, the target label. */
const LABEL_COLOR = "#68728a";
/** The scale's numbers. */
const SCALE_COLOR = "#8a93a6";
/** The target tick. */
const TICK_COLOR = "#5b6478";
/** The track behind a bar or an arc. */
const TRACK_COLOR = "#edf0f5";
/** The hairline over the footer. */
const RULE_COLOR = "#e6e9ef";
/** The gap pill when it judges nothing (a target, a budget). */
const PILL_BACKGROUND = "#eef1f5";
const PILL_TEXT = "#5b6478";
/** The gap pill of a guardrail: the bar's judgement, tinted. */
const PILL_ON_TRACK = { background: "rgba(46, 125, 50, 0.12)", text: DELTA_POSITIVE_COLOR };
const PILL_OFF_TRACK = { background: "rgba(198, 40, 40, 0.12)", text: DELTA_NEGATIVE_COLOR };

/** Inset of the words from the canvas edge. */
const PAD = 12;
/** Room under the plot for the footer line and the pill. */
const FOOTER_BOTTOM = 44;

// The canvas heights the pictures step down at (D6). A 3 x 8 tile gives about
// 270 px, the default 3 x 4 KPI tile about 100: most pictures live in the
// small one, so the compact step is a first-class layout, not a fallback.
/** Below this the footer and its rule go. */
const FULL_MIN_HEIGHT = 230;
/** Below this the eyebrow and the scale go, and the number moves top left. */
const ROOMY_MIN_HEIGHT = 150;
/** Below this canvas width (a 3-column tile on a tablet is ~165 px) the
 *  footer keeps only the gap, the number shrinks and the scale keeps its
 *  ends. A 3-column tile on a laptop (~280 px) keeps the full footer (lab
 *  gate, 2026-10-16). */
const WIDE_MIN_WIDTH = 260;
/** Up to this width the gauge's arc is drawn smaller, for the plan's word. */
const MID_MAX_WIDTH = 339;
/** The eyebrow's width on a narrow canvas: truncated with an ellipsis. */
const NARROW_TEXT_WIDTH = 140;

/**
 * A picture that is only words: the number (or "No data") with one line
 * under it. Used when a gauge refuses, and when a bullet or a gauge has no
 * actual to draw. Sized for the default KPI tile (3 x 4 cells), where a
 * 24 px number pushed the second line out of the canvas (D-2 gate).
 */
function textOnlyOption(text: string, subtext: string): EChartsOption {
  return {
    animation: false,
    title: {
      text,
      subtext,
      left: "center",
      top: "middle",
      itemGap: 6,
      textStyle: { fontSize: 20, fontWeight: "bold", color: INK },
      subtextStyle: { fontSize: 12, color: LABEL_COLOR, width: 260, overflow: "truncate" },
    },
    series: [],
  } as EChartsOption;
}

/** "Revenue · Target attainment", "Ad spend · Budget attainment to date",
 *  "Ad spend · Guardrail" (D3). */
export function pictureEyebrow(metricLabel: string, target: PictureTarget): string {
  const what =
    target.kind === "guardrail"
      ? "Guardrail"
      : `${KIND_LABEL[target.kind] ?? "Target"} attainment${target.to_date ? " to date" : ""}`;
  return metricLabel ? `${metricLabel} · ${what}` : what;
}

/** The footer's left word: "Target", "Budget to date", "Limit" for a capping
 *  guardrail, "Minimum" for a floor (D4). */
export function pictureFooterWord(target: PictureTarget): string {
  if (target.kind === "guardrail" && target.comparator === "lte") return "Limit";
  if (target.kind === "guardrail" && target.comparator === "gte") return "Minimum";
  return pictureTargetLabel(target);
}

/**
 * The gap pill (D4): how far the actual is from the plan, in the plan's own
 * words. "−€329,574 to target", "+€19,230 above target", "€6,533 over limit".
 * The magnitude is formatted unsigned and the sign is a word or a real minus:
 * the tile's formatter would write `€-329,574.00`. "On target" is decided on
 * the FORMATTED gap, so a pro-rated plan 40 cents off does not read "+€0".
 */
export function pictureGapLabel(
  actual: number,
  target: PictureTarget & { value: number },
  format: string | undefined,
  formatValue: (v: number) => string,
): string {
  const gap = actual - target.value;
  const magnitude = formatGapMagnitude(gap, format, formatValue);
  const zero = formatGapMagnitude(0, format, formatValue);
  if (target.kind === "guardrail" && target.comparator === "lte") {
    if (magnitude === zero) return "At the limit";
    return `${magnitude} ${gap > 0 ? "over" : "under"} limit`;
  }
  if (target.kind === "guardrail" && target.comparator === "gte") {
    if (magnitude === zero) return "At the minimum";
    return `${magnitude} ${gap > 0 ? "above" : "below"} minimum`;
  }
  const noun = target.kind === "budget" ? "budget" : target.kind === "guardrail" ? "guardrail" : "target";
  if (magnitude === zero) return `On ${noun}`;
  return gap > 0 ? `+${magnitude} above ${noun}` : `${MINUS}${magnitude} to ${noun}`;
}

/**
 * The picture's scale on a positive plan (D1): 0 to 120% of the plan, raised
 * in steps of 20% until the actual fits. `step` is the end as a share of the
 * plan, in fifths so it stays exact (1.2, never 1.2000000000000002).
 */
export function pictureScale(actual: number, plan: number): { max: number; step: number } {
  const fifths = Math.max(6, Math.ceil((actual / plan) * 5));
  return { max: (plan * fifths) / 5, step: fifths / 5 };
}

/** The footer: the plan on the left, the gap pill on the right, and the rule
 *  above them drawn as the line of an axis that spans the canvas (D4: no
 *  `graphic`, which the report sandbox does not register). */
function footerParts(
  actual: number,
  target: PictureTarget & { value: number },
  format: string | undefined,
  formatValue: (v: number) => string,
) {
  const tint = target.comparator == null ? null : bulletBarColor(actual, target, "") === DELTA_POSITIVE_COLOR ? PILL_ON_TRACK : PILL_OFF_TRACK;
  const titles = [
    {
      text: `${pictureFooterWord(target)} ${formatHeadline(target.value, format, formatValue)}`,
      left: PAD,
      bottom: 8,
      // The pill's padding, so both words sit on one line.
      padding: [5, 0],
      textStyle: { fontSize: 12, fontWeight: "normal" as const, color: LABEL_COLOR },
    },
    {
      text: pictureGapLabel(actual, target, format, formatValue),
      right: PAD,
      bottom: 8,
      padding: [5, 8],
      borderRadius: 6,
      backgroundColor: tint?.background ?? PILL_BACKGROUND,
      textStyle: { fontSize: 12, fontWeight: "normal" as const, color: tint?.text ?? PILL_TEXT },
    },
  ];
  const ruleGrid = { left: 0, right: 0, bottom: FOOTER_BOTTOM, height: 0, containLabel: false };
  const ruleAxes = (gridIndex: number) => ({
    xAxis: {
      type: "value" as const,
      gridIndex,
      min: 0,
      max: 1,
      axisLine: { show: true, onZero: false, lineStyle: { color: RULE_COLOR, width: 1 } },
      axisTick: { show: false },
      axisLabel: { show: false },
      splitLine: { show: false },
    },
    yAxis: { type: "value" as const, gridIndex, show: false },
  });
  return { titles, ruleGrid, ruleAxes };
}

/** The eyebrow title (D3); plain text, truncated (D10). */
function eyebrowTitle(metricLabel: string, target: PictureTarget) {
  return {
    text: pictureEyebrow(metricLabel, target),
    left: PAD,
    top: 10,
    textStyle: { fontSize: 12, fontWeight: "normal" as const, color: LABEL_COLOR, width: 300, overflow: "truncate" as const },
  };
}

/**
 * A bullet: one horizontal bar for the actual and a tick for the plan.
 *
 * Reads the target ONLY from `spec.picture_target` (D2 of the 2026-10-05
 * plan): never from a comparison leg, which is the viewer's switch and may be
 * off. Laid out as the 2026-10-16 mockup: the eyebrow, the number and its
 * share, the bar on a 0-to-120%-of-plan scale with the tick labelled, and a
 * footer with the plan and the gap. Without a positive plan, or with a
 * negative actual, the bar still draws on a scale over the values, with the
 * reason under the number and no footer: the sentence comes from a closed
 * map, so the server's reason string is never interpolated anywhere.
 */
export function buildBulletOption({ spec, neutralColor, seriesColors, painted, formatValue }: KpiPictureInput): EChartsOption {
  const key = spec.y_keys?.[0] ?? "";
  const actual = key ? finite(spec.rows?.[0]?.[key]) : null;
  const metricLabel = spec.labels?.[key] ?? key;
  const format = spec.metric_format;
  const target = readPictureTarget(spec);
  const targetLabel = pictureTargetLabel(target);
  const color = seriesColors?.measureOverride ?? bulletBarColor(actual, target, seriesColors?.measure ?? neutralColor);
  const tickColor = seriesColors?.target ?? TICK_COLOR;
  painted?.("measure", seriesColors?.measureOverride ?? seriesColors?.measure ?? neutralColor);
  if (target.value != null) painted?.("target", tickColor);
  const missingSentence = target.value == null ? targetReasonSentence(target.reason, target.lines_range) : null;
  // No actual (no rows, or a period with no data yet): a bar of nothing and a
  // bare axis read as a broken tile (D-2 gate, 2026-10-06), so say it.
  if (actual == null) {
    return textOnlyOption(
      "No data",
      missingSentence ?? `${targetLabel}: ${formatValue(target.value as number)}`,
    );
  }

  const tooltipHtml = (): string => {
    const lines = [`<strong>${escapeHtml(metricLabel)}</strong>`];
    lines.push(`Actual: ${escapeHtml(formatValue(actual))}`);
    lines.push(
      target.value == null
        ? escapeHtml(missingSentence ?? "")
        : `${escapeHtml(targetLabel)}: ${escapeHtml(formatValue(target.value))}`,
    );
    return lines.join("<br/>");
  };

  // The plan scales the picture only when it is positive and the bar starts
  // at 0 (D1); otherwise the bar draws on a scale over the values.
  const planned = target.value != null && target.value > 0 && actual >= 0
    ? (target as PictureTarget & { value: number })
    : null;
  const scale = planned ? pictureScale(actual, planned.value) : null;
  const fallbackMax = axisMax([actual, target.value ?? 0]);
  const headlineText = formatHeadline(actual, format, formatValue);
  const subline = planned
    ? gaugeShareLabel(actual, planned)
    : missingSentence ?? `${targetLabel}: ${formatValue(target.value as number)}`;
  const footer = planned ? footerParts(actual, planned, format, formatValue) : null;

  const headline = {
    text: headlineText,
    subtext: subline,
    left: PAD,
    top: 30,
    itemGap: 6,
    textStyle: { fontSize: 28, fontWeight: "bold" as const, color: INK },
    subtextStyle: { fontSize: 12, color: LABEL_COLOR, width: 300, overflow: "truncate" as const },
  };
  // Title order is fixed, so a media step can address each by index:
  // eyebrow, headline, then the footer's plan and pill.
  const titles = [eyebrowTitle(metricLabel, target), headline, ...(footer?.titles ?? [])];

  const barGrid = { left: PAD, right: PAD, top: 120, bottom: footer ? 104 : 40 };
  const valueAxis = {
    type: "value" as const,
    min: Math.min(0, actual),
    ...(scale ? { max: scale.max, interval: scale.max / 2 } : fallbackMax !== undefined ? { max: fallbackMax } : {}),
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
    axisLabel: {
      // The scale's three numbers only on the plan's scale; a fallback
      // scale over the values says nothing a reader can compare with.
      show: scale != null,
      formatter: (v: number) => formatCompact(v, format),
      color: SCALE_COLOR,
      fontSize: 11,
      margin: 10,
      alignMinLabel: "left",
      alignMaxLabel: "right",
    },
  };
  const categoryAxis = { type: "category" as const, data: [metricLabel], show: false };

  const option = {
    animation: false,
    title: titles,
    grid: footer ? [barGrid, footer.ruleGrid] : [barGrid],
    tooltip: { trigger: "item", formatter: tooltipHtml },
    xAxis: footer ? [valueAxis, footer.ruleAxes(1).xAxis] : [valueAxis],
    yAxis: footer ? [categoryAxis, footer.ruleAxes(1).yAxis] : [categoryAxis],
    series: [
      {
        type: "bar",
        name: metricLabel,
        data: [actual],
        barWidth: 14,
        itemStyle: { color, borderRadius: 7 },
        // The track to the axis end, so a short bar still reads as a share.
        showBackground: true,
        backgroundStyle: { color: TRACK_COLOR, borderRadius: 7 },
      },
      // The tick: a thin upright mark at the plan, taller than the bar. A
      // symbol, not a markLine: a markLine on a one-category axis spans only
      // the category's band, which is the bar's own height (lab gate,
      // 2026-10-16). Scatter is registered on every surface (`ECHARTS_USE`).
      ...(target.value != null
        ? [
            {
              type: "scatter",
              name: targetLabel,
              silent: true,
              z: 3,
              data: [[target.value, 0]],
              symbol: "rect",
              symbolSize: [2, 34],
              itemStyle: { color: tickColor },
              // "Target €798K" over the tick: a function, so the words are
              // never a `{…}` template (D10).
              label: {
                show: planned != null,
                position: "top",
                distance: 4,
                formatter: () => `${pictureFooterWord(target)} ${formatCompact(target.value as number, format)}`,
                color: LABEL_COLOR,
                fontSize: 11,
              },
            },
          ]
        : []),
    ],
    media: [
      {
        // A mid-size tile: no footer, the bar moves down into its room.
        query: { maxHeight: FULL_MIN_HEIGHT - 1 },
        option: {
          title: [{}, {}, { show: false }, { show: false }],
          grid: footer ? [{ top: 108, bottom: 34 }, { show: false }] : [{ top: 108, bottom: 34 }],
          xAxis: footer ? [{}, { show: false }] : [{}],
        },
      },
      {
        // The default KPI tile: the number top left, the bar under it, no
        // eyebrow, no scale, no tick label (D6).
        query: { maxHeight: ROOMY_MIN_HEIGHT - 1 },
        option: {
          title: [
            { show: false },
            { top: 4, itemGap: 4, textStyle: { fontSize: 18 }, subtextStyle: { fontSize: 11 } },
          ],
          grid: [{ top: 46, bottom: 6 }],
          xAxis: [{ axisLabel: { show: false } }],
          series: [{ barWidth: 12 }, ...(target.value != null ? [{ symbolSize: [2, 22], label: { show: false } }] : [])],
        },
      },
      {
        // A narrow tile: the scale keeps its two ends, the tick its mark
        // without words, the footer only the gap.
        query: { maxWidth: WIDE_MIN_WIDTH - 1 },
        option: {
          title: [
            { textStyle: { width: NARROW_TEXT_WIDTH } },
            { textStyle: { fontSize: 22 }, subtextStyle: { width: NARROW_TEXT_WIDTH } },
            { show: false },
            { right: undefined, left: PAD },
          ],
          xAxis: [scale ? { interval: scale.max } : {}],
          series: [{}, ...(target.value != null ? [{ label: { show: false } }] : [])],
        },
      },
    ],
  };
  return option as unknown as EChartsOption;
}

/** The gauge's scale on a positive plan: the same rule as the bullet (D1). */
export function gaugeMax(actual: number, target: number): number {
  return pictureScale(actual, target).max;
}

/**
 * Why a gauge cannot be drawn honestly, or null when it can. An arc starts at
 * 0, so a target that is not positive or an actual below 0 has no gauge; an
 * absent target has none either, and the gauge never invents a maximum.
 */
export function gaugeRefusal(actual: number | null, target: PictureTarget): string | null {
  if (target.value == null) return targetReasonSentence(target.reason, target.lines_range);
  if (!(target.value > 0)) return "A gauge needs a positive target";
  if (actual != null && actual < 0) return "A gauge cannot draw a value below 0";
  return null;
}

/** "58.7% of target", "64.0% of budget to date", "91.2% of limit". A
 *  guardrail reads as the line it draws, a limit (`lte`) or a minimum
 *  (`gte`), as the footer does. One decimal, always (D2). */
export function gaugeShareLabel(
  actual: number,
  target: PictureTarget & { value: number },
  { short = false }: { short?: boolean } = {},
): string {
  const noun =
    target.kind === "guardrail" && target.comparator === "lte"
      ? "limit"
      : target.kind === "guardrail" && target.comparator === "gte"
        ? "minimum"
        : (target.kind || "target");
  return `${formatShare(actual, target.value)} of ${noun}${target.to_date && !short ? " to date" : ""}`;
}

/** Where on the arc a value sits, in degrees: 225 at 0, -45 at the end. */
const ARC_START = 225;
const ARC_SWEEP = 270;
function arcAngle(value: number, max: number): number {
  return ARC_START - ARC_SWEEP * Math.min(Math.max(value / max, 0), 1);
}

/** A point at `distance` radii from the centre at `angle`, as the percent
 *  pair a gauge's `offsetCenter` takes (ECharts reads it against the radius,
 *  `GaugeView.js`: `parsePercent(offsetCenter, posInfo.r)`), so it holds on
 *  every canvas without measuring it. */
function polarOffset(angle: number, distance: number): [string, string] {
  const rad = (angle * Math.PI) / 180;
  return [`${(Math.cos(rad) * distance * 100).toFixed(1)}%`, `${(-Math.sin(rad) * distance * 100).toFixed(1)}%`];
}

/**
 * A gauge: an arc from 0 filled to the actual, and a tick at the plan.
 *
 * Same reading rules as the bullet: the target comes only from
 * `spec.picture_target`, and the colour judges only a guardrail. When the
 * gauge refuses (no target, a target at or below 0, a negative actual) the
 * tile prints the number with the reason under it, so it is never blank and
 * never a made-up scale. Laid out as the 2026-10-16 mockup: the eyebrow, the
 * number and its share inside the arc, `0` and the scale's end under the
 * arc's ends, the tick outside the arc with the plan's word beside it, and
 * the footer with the plan and the gap.
 */
export function buildGaugeOption({ spec, neutralColor, seriesColors, painted, formatValue }: KpiPictureInput): EChartsOption {
  const key = spec.y_keys?.[0] ?? "";
  const actual = key ? finite(spec.rows?.[0]?.[key]) : null;
  const metricLabel = spec.labels?.[key] ?? key;
  const format = spec.metric_format;
  const target = readPictureTarget(spec);
  const targetLabel = pictureTargetLabel(target);
  const refusal = gaugeRefusal(actual, target);
  const actualText = actual == null ? "No data" : formatValue(actual);

  if (refusal != null || actual == null) {
    // Text only; ECharts draws `title` as text, never as HTML.
    return textOnlyOption(
      actual == null ? actualText : formatHeadline(actual, format, formatValue),
      refusal ?? `${targetLabel}: ${formatValue(target.value as number)}`,
    );
  }

  const planned = target as PictureTarget & { value: number };
  const { max, step } = pictureScale(actual, planned.value);
  const color = seriesColors?.measureOverride ?? bulletBarColor(actual, planned, seriesColors?.measure ?? neutralColor);
  const tickColor = seriesColors?.target ?? TICK_COLOR;
  painted?.("measure", seriesColors?.measureOverride ?? seriesColors?.measure ?? neutralColor);
  painted?.("target", tickColor);
  const share = gaugeShareLabel(actual, planned);
  // Inside the arc the line has the arc's inner width: "to date" is in the
  // eyebrow and the footer already, so the arc says the short form (lab gate).
  const shortShare = gaugeShareLabel(actual, planned, { short: true });
  const headlineText = formatHeadline(actual, format, formatValue);
  const footer = footerParts(actual, planned, format, formatValue);

  const tooltipHtml = (): string =>
    [
      `<strong>${escapeHtml(metricLabel)}</strong>`,
      `Actual: ${escapeHtml(actualText)}`,
      `${escapeHtml(targetLabel)}: ${escapeHtml(formatValue(planned.value))}`,
      escapeHtml(share),
    ].join("<br/>");

  // A three-quarter arc. ECharts reads a gauge radius as a share of the
  // SMALLER half of the canvas, so a circle is the one shape that fits every
  // tile (D-2 gate, 2026-10-06). Every label below is placed in percent of
  // that radius, so the media steps only move the centre and the radius.
  const arc = { startAngle: ARC_START, endAngle: ARC_START - ARC_SWEEP, min: 0, max, radius: "66%", center: ["50%", "49%"] };
  const hidden = {
    axisTick: { show: false },
    splitLine: { show: false },
    axisLabel: { show: false },
    anchor: { show: false },
  };
  const targetAngle = arcAngle(planned.value, max);
  // The plan's word on the arc is the short one ("Target", "Budget",
  // "Limit"); "to date" is in the eyebrow and the footer already.
  const arcWord =
    target.kind === "guardrail" && target.comparator != null ? pictureFooterWord(target) : (KIND_LABEL[target.kind] ?? "Target");
  // The word sits beyond the tick, centred on its point; a horizontal word
  // needs more room at the sides than at the top, about 3.5% of the radius
  // per character at the full size's ~100 px radius.
  const wordDistance = 1.24 + Math.abs(Math.cos((targetAngle * Math.PI) / 180)) * 0.035 * arcWord.length;
  const endLabel = { fontSize: 11, color: SCALE_COLOR };
  // A media step replaces series data whole, so the narrow step restates
  // the first two of these rather than hiding the third.
  const scaleWords = [
    { value: 0, name: "0", title: { offsetCenter: [polarOffset(ARC_START, 1)[0], "88%"] } },
    { value: max, name: `${Math.round(step * 100)}%`, title: { offsetCenter: [polarOffset(ARC_START - ARC_SWEEP, 1)[0], "88%"] } },
    {
      value: planned.value,
      name: arcWord,
      title: { offsetCenter: polarOffset(targetAngle, wordDistance), color: LABEL_COLOR },
    },
  ];

  return {
    animation: false,
    title: [eyebrowTitle(metricLabel, target), ...footer.titles],
    grid: [footer.ruleGrid],
    xAxis: [footer.ruleAxes(0).xAxis],
    yAxis: [footer.ruleAxes(0).yAxis],
    tooltip: { trigger: "item", formatter: tooltipHtml },
    series: [
      {
        type: "gauge",
        name: metricLabel,
        ...arc,
        ...hidden,
        progress: { show: true, width: 18, itemStyle: { color } },
        axisLine: { lineStyle: { width: 18, color: [[1, TRACK_COLOR]] } },
        pointer: { show: false },
        // The number and its share inside the arc. A function formatter, so
        // the number is never a `{…}` template (D10).
        detail: {
          show: true,
          offsetCenter: [0, "-9%"],
          fontSize: 24,
          fontWeight: "bold",
          color: INK,
          formatter: () => headlineText,
        },
        title: { show: true, offsetCenter: [0, "16%"], fontSize: 12, color: LABEL_COLOR },
        data: [{ value: actual, name: shortShare }],
      },
      {
        // The tick: a second gauge on the same scale whose only visible part
        // is a short pointer at the target, just outside the arc.
        type: "gauge",
        name: targetLabel,
        ...arc,
        ...hidden,
        silent: true,
        axisLine: { show: false },
        progress: { show: false },
        title: { show: false },
        detail: { show: false },
        pointer: {
          show: true,
          icon: "rect",
          width: 2,
          length: "18%",
          offsetCenter: [0, "-104%"],
          itemStyle: { color: tickColor },
        },
        data: [{ value: planned.value, name: targetLabel }],
      },
      {
        // The scale's words: `0` under the start, the end's share under the
        // end, the plan's word beyond the tick. Each is a data item's title
        // on a series that draws nothing else and answers no hover.
        type: "gauge",
        name: "scale",
        ...arc,
        ...hidden,
        silent: true,
        axisLine: { show: false },
        progress: { show: false },
        pointer: { show: false },
        detail: { show: false },
        title: { show: true, ...endLabel },
        data: scaleWords,
      },
    ],
    media: [
      {
        // A mid-size tile: no footer; the arc takes the room it leaves.
        query: { maxHeight: FULL_MIN_HEIGHT - 1 },
        option: {
          title: [{}, { show: false }, { show: false }],
          xAxis: [{ show: false }],
          series: [{ center: ["50%", "54%"], radius: "76%" }, { center: ["50%", "54%"], radius: "76%" }, { center: ["50%", "54%"], radius: "76%" }],
        },
      },
      {
        // The default KPI tile: the number and its share top left, a small
        // arc to the right of them, no words on the arc (D6).
        query: { maxHeight: ROOMY_MIN_HEIGHT - 1 },
        option: {
          title: [
            {
              text: headlineText,
              subtext: shortShare,
              top: "middle",
              itemGap: 4,
              textStyle: { fontSize: 18, fontWeight: "bold", color: INK, width: undefined, overflow: "none" },
              subtextStyle: { fontSize: 11, color: LABEL_COLOR },
            },
          ],
          series: [
            { center: ["80%", "58%"], radius: "94%", progress: { width: 9 }, axisLine: { lineStyle: { width: 9 } }, detail: { show: false }, title: { show: false } },
            { center: ["80%", "58%"], radius: "94%", pointer: { length: "22%", offsetCenter: [0, "-106%"] } },
            { center: ["80%", "58%"], radius: "94%", title: { show: false } },
          ],
        },
      },
      {
        // A laptop's 3-column tile: the arc a little smaller, so the plan's
        // word beside it stays inside the canvas.
        query: { minWidth: WIDE_MIN_WIDTH, maxWidth: MID_MAX_WIDTH, minHeight: ROOMY_MIN_HEIGHT },
        option: {
          series: [{ radius: "58%", detail: { fontSize: 21 } }, { radius: "58%" }, { radius: "58%" }],
        },
      },
      {
        // A narrow tile: a smaller number in a thinner arc, the plan's word
        // off the arc, the footer only the gap.
        query: { maxWidth: WIDE_MIN_WIDTH - 1 },
        option: {
          title: [{ textStyle: { width: NARROW_TEXT_WIDTH } }, { show: false }, { right: undefined, left: PAD }],
          series: [
            { progress: { width: 12 }, axisLine: { lineStyle: { width: 12 } }, detail: { fontSize: 17 }, title: { fontSize: 10 } },
            { pointer: { length: "14%" } },
            { data: scaleWords.slice(0, 2) },
          ],
        },
      },
      {
        // Narrow AND short: the number takes the left, so the arc gets
        // smaller and moves to the edge rather than over it.
        query: { maxWidth: WIDE_MIN_WIDTH - 1, maxHeight: ROOMY_MIN_HEIGHT - 1 },
        option: {
          title: [{ textStyle: { fontSize: 16 } }],
          // The radius is a share of HALF the canvas height, the centre of
          // its width: 60% of ~50 px at 78% of ~165 px stays inside the edge.
          series: [0, 1, 2].map(() => ({ center: ["78%", "58%"], radius: "60%" })),
        },
      },
    ],
  } as unknown as EChartsOption;
}

/**
 * How every KPI picture is built. Keyed on the registry-derived union, so a
 * KPI row that flips without an entry here does not compile.
 */
const KPI_PICTURE_BUILDERS: Record<KpiChartType | PreparedLibraryKpiChartType, KpiPictureBuilder> = {
  bullet: buildBulletOption,
  gauge: buildGaugeOption,
};

/** Every chart type this leg draws, flipped or still on `library`. */
export const KPI_PICTURE_CHART_TYPES: readonly string[] = Object.freeze(Object.keys(KPI_PICTURE_BUILDERS));

export function isKpiPictureChartType(chartType: unknown): boolean {
  return (
    typeof chartType === "string" &&
    Object.prototype.hasOwnProperty.call(KPI_PICTURE_BUILDERS, chartType.toLowerCase())
  );
}

/** The option for a KPI picture; the caller checked `isKpiPictureChartType`. */
export function buildKpiPictureOption(chartType: string, input: KpiPictureInput): EChartsOption {
  return KPI_PICTURE_BUILDERS[chartType.toLowerCase() as keyof typeof KPI_PICTURE_BUILDERS](input);
}
